/**
 * @fileoverview FlowEditor
 * @description The pipeline itself, as an editable list.
 *
 *              Order is the whole point of this module — a hop is defined by
 *              which two checkpoints sit next to each other — so reordering is
 *              a first-class action rather than a hidden setting. Move is done
 *              with buttons instead of drag-and-drop: it works with a
 *              keyboard, works on touch, and needs no extra dependency.
 *
 *              Disabling a checkpoint does not delete it. Narrowing the chain
 *              to "AIMS → DB" to prove a hop is a routine diagnostic step, and
 *              having to rebuild the checkpoint afterwards would discourage it.
 */

import React, { useMemo } from 'react';
import {
  Box, Button, Chip, Divider, IconButton, Paper, Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowDown, ArrowRight, ArrowUp, Database, FileCode, Globe, KeyRound, Pencil, Plus, Trash2,
} from 'lucide-react';

const TEAL = '#0097A9';
const NAVY = '#01426A';

const KIND_META = {
  soap_xml: { Icon: FileCode, label: 'SOAP', color: '#7c3aed', bg: '#f5f3ff' },
  rest_json: { Icon: Globe, label: 'REST', color: '#0891b2', bg: '#ecfeff' },
  sql_query: { Icon: Database, label: 'SQL', color: '#c2410c', bg: '#fff7ed' },
};

/** A one-line summary of where a checkpoint points, without its secrets. */
function targetOf(checkpoint) {
  if (checkpoint.kind === 'sql_query') {
    const { server, port, database } = checkpoint.config || {};
    if (!server) return 'no server set';
    return `${server}${port ? `,${port}` : ''} · ${database || '?'}`;
  }
  const url = checkpoint.config?.url || '';
  try {
    const parsed = new URL(url.replace(/\{[a-z_]+\}/g, 'x'));
    return `${parsed.hostname}${parsed.pathname}`;
  } catch {
    return url || 'not configured';
  }
}

/**
 * Whether a checkpoint is missing a credential it actually needs.
 *
 * A half-filled pair is always wrong — a SQL username with no password cannot
 * connect — while leaving every optional credential blank is a legitimate
 * choice (an open API, or integrated authentication).
 */
function credentialGap(checkpoint, schema) {
  const secrets = schema?.secrets || [];
  if (!secrets.length) return false;

  const isSet = (key) =>
    Boolean(checkpoint.secretsSet?.[key]) || Boolean(checkpoint.secrets?.[key]);

  if (secrets.some((secret) => secret.required && !isSet(secret.key))) return true;

  const filled = secrets.filter((secret) => isSet(secret.key)).length;
  return filled > 0 && filled < secrets.length;
}

function CheckpointRow({ checkpoint, index, total, hasSecretGap, onMove, onToggle, onEdit, onDelete }) {
  const meta = KIND_META[checkpoint.kind] || KIND_META.rest_json;
  const { Icon } = meta;
  const off = !checkpoint.enabled;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75, borderRadius: '12px', border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${off ? '#cbd5e1' : meta.color}`,
        opacity: off ? 0.6 : 1,
        transition: 'opacity 160ms ease, border-color 160ms ease',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        {/* Order controls */}
        <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
          <Stack spacing={0}>
            <IconButton
              size="small" disabled={index === 0} onClick={() => onMove(index, -1)}
              aria-label={`Move ${checkpoint.label} earlier`}
              sx={{ p: 0.25, color: '#64748b' }}
            >
              <ArrowUp size={14} />
            </IconButton>
            <IconButton
              size="small" disabled={index === total - 1} onClick={() => onMove(index, 1)}
              aria-label={`Move ${checkpoint.label} later`}
              sx={{ p: 0.25, color: '#64748b' }}
            >
              <ArrowDown size={14} />
            </IconButton>
          </Stack>
          <Box
            sx={{
              width: 34, height: 34, borderRadius: '10px', bgcolor: meta.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon size={17} color={meta.color} />
          </Box>
        </Stack>

        {/* Identity */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={800} color={NAVY} noWrap>
              {checkpoint.label}
            </Typography>
            <Chip
              size="small" label={checkpoint.short}
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#eff6ff', color: NAVY }}
            />
            <Chip
              size="small" label={meta.label}
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: meta.bg, color: meta.color }}
            />
            {hasSecretGap && (
              <Tooltip title="This checkpoint has no credential yet — click to add one.">
                <Chip
                  size="small" clickable icon={<KeyRound size={11} />} label="Add credential"
                  onClick={() => onEdit(index)}
                  sx={{
                    height: 20, fontSize: '0.62rem', fontWeight: 800,
                    bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d',
                    '& .MuiChip-icon': { color: '#b45309' },
                    '&:hover': { bgcolor: '#fef3c7' },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography
            noWrap
            sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#64748b', mt: 0.25 }}
          >
            {targetOf(checkpoint)}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
          <Tooltip title={off ? 'Include in the chain' : 'Skip this checkpoint'}>
            <Switch
              size="small"
              checked={Boolean(checkpoint.enabled)}
              onChange={() => onToggle(index)}
              slotProps={{ input: { 'aria-label': `Include ${checkpoint.label} in the chain` } }}
              sx={{
                '& .Mui-checked': { color: TEAL },
                '& .Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
              }}
            />
          </Tooltip>
          <Tooltip title="Edit checkpoint">
            <IconButton size="small" onClick={() => onEdit(index)} sx={{ color: TEAL }}>
              <Pencil size={15} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove checkpoint">
            <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#dc2626' }}>
              <Trash2 size={15} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

const FlowEditor = ({ flow, kinds, onChange, onEdit, onAdd }) => {
  const checkpoints = useMemo(() => flow?.checkpoints || [], [flow]);

  const move = (index, delta) => {
    const next = [...checkpoints];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ ...flow, checkpoints: next });
  };

  const toggle = (index) =>
    onChange({
      ...flow,
      checkpoints: checkpoints.map((c, i) => (i === index ? { ...c, enabled: !c.enabled } : c)),
    });

  const remove = (index) =>
    onChange({ ...flow, checkpoints: checkpoints.filter((_, i) => i !== index) });

  const enabled = useMemo(() => checkpoints.filter((c) => c.enabled), [checkpoints]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={1.5}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={800} color={NAVY}>
            Pipeline
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Each neighbouring pair becomes a hop. Reorder or switch a checkpoint off to change what gets compared.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0}>
          {(kinds || []).map((kind) => {
            const meta = KIND_META[kind.kind] || KIND_META.rest_json;
            return (
              <Tooltip key={kind.kind} title={kind.description} describeChild>
                <Button
                  size="small" variant="outlined" startIcon={<Plus size={13} />}
                  onClick={() => onAdd(kind.kind)}
                  sx={{
                    borderColor: meta.color, color: meta.color, borderRadius: '8px',
                    textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                    '&:hover': { borderColor: meta.color, bgcolor: meta.bg },
                  }}
                >
                  {meta.label}
                </Button>
              </Tooltip>
            );
          })}
        </Stack>
      </Stack>

      {/* Hop preview: what the run will actually compare. */}
      <Stack
        direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap
        sx={{ mb: 1.75, p: 1.25, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}
      >
        {enabled.length < 2 ? (
          <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600 }}>
            Enable at least two checkpoints — a single checkpoint has nothing to compare against.
          </Typography>
        ) : (
          enabled.map((checkpoint, index) => (
            <React.Fragment key={checkpoint.id || index}>
              <Chip
                size="small" label={checkpoint.short}
                sx={{ fontWeight: 800, fontSize: '0.68rem', bgcolor: '#fff', border: '1px solid #cbd5e1' }}
              />
              {index < enabled.length - 1 && <ArrowRight size={13} color="#94a3b8" />}
            </React.Fragment>
          ))
        )}
      </Stack>

      <Stack spacing={1.25}>
        {checkpoints.map((checkpoint, index) => (
          <CheckpointRow
            key={checkpoint.id || index}
            checkpoint={checkpoint}
            index={index}
            total={checkpoints.length}
            hasSecretGap={credentialGap(
              checkpoint,
              (kinds || []).find((k) => k.kind === checkpoint.kind),
            )}
            onMove={move}
            onToggle={toggle}
            onEdit={onEdit}
            onDelete={remove}
          />
        ))}
        {!checkpoints.length && (
          <>
            <Divider />
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No checkpoints yet. Add one to start building the chain.
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default FlowEditor;
